import { UIElement } from '../ui-element';
import { Labels } from '../../global/labels';
import * as UIDropdownHelpers from '../dropdown/ui-dropdown.helpers';
import * as UISliderHelpers from '../slider/ui-slider.helpers';
import * as UIDatePickerHelpers from '../datetime/ui-datepicker.helpers';
import * as UIAutocompleteHelpers from '../autocomplete/ui-autocomplete.helpers';
import { UIHint } from '../hint/ui-hint';
import {
    getData,
    setData,
    mergeAttributes,
    BaseNativeFormElements,
    NativeFormElements,
    CustomFormElements,
    element,
    keepAllowedElements,
    createElement,
    updateElement,
} from '../../global/ui-helpers';
import { Digest } from '../../global/digest';
import { getDebugMode } from '../../global/config';
import { updateAriaLabels, handleLabelClick } from './ui-field-accessibility';
import { browser } from '../../global/browser';
import { debounce } from '../../global/helpers';
import { Component } from '../../decorators/component.decorator';
import { Attribute } from '../../decorators/attribute.decorator';
import { Child } from '../../decorators/child.decorator';
import type {
    AttributesConfig,
    AttributeValue,
    ElementConfig,
    ElementChild,
    ElementChildren,
    ObservedAttributes,
} from '../ui-element.type';
import type {
    FieldControl,
    FieldConfig,
    FieldHint,
} from '../fieldset/ui-fieldset.type';
import type { TranslationLabels } from '../../types/labels.type';
import type { ElementControlsData, NullOr } from '../../global/utils.type';
import type { Debounce } from '../../global/utils.type';
import type {
    BuildControlResult,
    ControlWithFieldId,
    UIFieldLayout,
    UIFieldLayouts,
} from './ui-field.type';
import type { UIDropdown } from '../dropdown/ui-dropdown';
import { UIInputGroup } from '../inputgroup/ui-inputgroup';
import styles from './ui-field.css';

/**
 * @class
 * @memberof SharedComponents
 * @augments {UIElement}
 * @alias UIField
 * @element ui-field
 * @classdesc Represents a class for <code>ui-field</code> element.
 * Container for input elements, ui-field is very useful element that
 * handles errors, gives more opportunities to input elements. If you are
 * working with form, it is strongly recommended to wrap all input with
 * ui-field.
 * @property {boolean} [required] {@attr required} If true,
 * a required field indicator ("(Required)") will be added to the label.
 * @property {string} [label] {@attr label} Label of the field.
 * @property {string} [units] {@attr units} Text for units.
 * @property {string} [describedById] {@attr described-by-id} Id or ids (separated with space) of an element or elements that provide a longer description for the field via <code>aria-describedby</code> attribute on the field's input.
 * Usually to be used with <code>ui-tooltip</code> or <code>ui-hint</code>, although it is fine to use it with any other element.
 * When left empty, <code>ui-field</code> tries to reuse or generate IDs for <code>ui-hint</code> and <code>ui-tooltip</code> and update <code>aria-describedby</code> value based on those.
 * @property {UIFieldLayouts} [layout] {@attr layout} Layout of field.
 *  {@desc fluid: Full width container.}
 *  {@desc inline: Field and label are aligned to left with auto width.}
 *  {@desc equal: Label and field are aligned to left and have 50% width.}
 * @property {boolean} [actionAdd] {@attr action-add} Adds button with only icon plus. Button has data-event=ui-field-add.
 * @property {boolean} [actionRemove] {@attr action-remove} Adds button with only icon cross. Button has data-event=ui-field-remove.
 * @property {HTMLDivElement} control Shortcut for control wrapper.
 * Wrapper can contain many inputs.
 * @property {HTMLDivElement} labelContainer Shortcut for label wrapper.
 * Wrapper can contain labels and tooltips.
 * @property {HTMLElement} trailingAction Shortcut for trailing action wrapper.
 * Contains add/remove or custom trailing action button.
 * @property {HTMLElement} inputGroup Shortcut for ui-inputgroup wrapper.
 * Contains controls and trailing actions, except hints.
 * @property {HTMLElement} unitsElement Shortcut for units element.
 * @slot
 * @example
 * <ui-field required label="Your name">
 *   <input type="text" name="your-name">
 * </ui-field>
 */
@Component({ tag: 'ui-field', styles })
export class UIField extends UIElement {
    @Attribute(Boolean) required?: boolean;
    @Attribute(String) label?: string;
    @Attribute(String) layout?: UIFieldLayout;
    @Attribute(Boolean) actionAdd?: boolean;
    @Attribute(Boolean) actionRemove?: boolean;
    @Attribute(String) units?: string;

    @Child('.ui-field__label') labelContainer?: HTMLDivElement;
    @Child('.ui-field__control') control?: HTMLDivElement;
    @Child('.ui-field__trailing-action') trailingAction?: HTMLElement;
    @Child('.ui-field__inputgroup') inputGroup?: UIInputGroup;
    @Child('.ui-field__units') unitsElement?: HTMLSpanElement;

    private _labelClickListener: {
        label: HTMLElement;
        handler: EventListener;
    } | null = null;

    private _unitsWrapper: HTMLSpanElement | null = null;
    private resizeHandler?: Debounce;

    /**
     * Layout type of field.
     * @type {UIFieldLayouts}
     * @readonly
     */
    static readonly Layout: UIFieldLayouts = {
        Fluid: 'fluid',
        Inline: 'inline',
        Equal: 'equal',
    } as const;

    /**
     * Define labels what could be localised
     * @type {Labels}
     * @readonly
     */
    static get labels(): TranslationLabels.Labels {
        return Labels.attach('ui-field', {
            required: 'Required',
            add: 'Add',
            remove: 'Remove',
        });
    }

    /**
     * @type {string[]}
     */
    static get observedAttributes(): ObservedAttributes {
        return ['label', 'required', 'action-add', 'action-remove', 'units'];
    }

    /**
     * @type {string[]}
     * @readonly
     */
    static get allowedTrailingElementTags(): Array<string> {
        return ['a', 'ui-link', 'button', 'ui-button'];
    }

    /**
     * Convert data config to element config
     * @param {FieldConfig} config
     * @returns {ElementConfig}
     */
    static fromFieldConfig(config: FieldConfig): ElementConfig {
        let controls: Array<ElementConfig | false | null> = [];

        if (config.control) {
            const ctrl = UIField.buildControlFromConfig(config.control);
            if (Array.isArray(ctrl)) {
                controls = controls.concat(ctrl);
            } else {
                controls.push(ctrl);
            }
        }

        if (config.controls) {
            Object.keys(config.controls).forEach((key) => {
                const control = config.controls![key];
                const ctrl = UIField.buildControlFromConfig(control);
                if (config.invalid && control.attributes) {
                    // AttributesConfig type doesn't let us to set an attribute by name
                    (
                        control.attributes as AttributesConfig & {
                            'aria-invalid'?: 'true' | 'false';
                        }
                    )['aria-invalid'] = 'true';
                }
                if (Array.isArray(ctrl)) {
                    controls = controls.concat(ctrl);
                } else {
                    controls.push(ctrl);
                }
            });
        }

        if (Array.isArray(config.hints) && config.hints.length > 0) {
            config.hints.forEach((hint: FieldHint) => {
                controls.push({
                    tagName: hint.type !== 'span' ? 'ui-hint' : 'span',
                    attributes: hint.type
                        ? {
                              'type': hint.type,
                              'error-type': hint.errorType || null,
                          }
                        : undefined,
                    children: [hint.content ?? ''],
                });
            });
        }

        if (config.tooltip) {
            controls.push({
                tagName: 'ui-tooltip',
                attributes: {
                    type: 'question',
                    slot: 'label',
                },
                children: [config.tooltip.content],
            });
        }

        const isControlRequired = (ctrl: ElementConfig): boolean => {
            if (
                ctrl.attributes &&
                (ctrl.attributes as Record<string, unknown>).required
            ) {
                return true;
            }
            if (Array.isArray(ctrl.children) && ctrl.children.length) {
                return (ctrl.children as Array<ElementConfig>).some(
                    isControlRequired
                );
            }
            return false;
        };

        return {
            tagName: 'ui-field',
            attributes: {
                'key': 'id' in config ? config.id : null,
                'label': config.label,
                'data-index': 'index' in config ? config.index : null,
                'aria-hidden':
                    'visible' in config && !config.visible ? 'true' : null,
                'required':
                    (controls.filter(Boolean) as Array<ElementConfig>).some(
                        isControlRequired
                    ) || null,
                'visible': !!config.visible,
                'invalid': !!config.invalid,
            },
            classList: {
                '-hidden': 'visible' in config ? !config.visible : false,
                '-error': 'invalid' in config ? config.invalid : false,
            },
            children: controls as ElementChildren,
        };
    }

    /**
     * Create an ElementConfig based on FieldControl.
     * @param {FieldControl} control
     * @returns {ElementConfig | Array<ElementConfig>}
     */
    static buildControlFromConfig(control: FieldControl): BuildControlResult {
        const inputControls: Array<FieldControl> = [];
        switch (control.type) {
            case 'input':
                return {
                    tagName: 'input',
                    attributes: mergeAttributes(
                        {
                            type: 'text',
                            value: 'value' in control ? control.value : null,
                        },
                        control.attributes as Record<string, unknown>
                    ),
                    classList: control.classList || {},
                };
            case 'textarea':
                return {
                    tagName: 'textarea',
                    attributes: control.attributes,
                };
            case 'select': {
                const children =
                    'options' in control && control.options
                        ? control.options.map(
                              (option): ElementConfig => ({
                                  tagName: 'option',
                                  attributes: {
                                      value: option.value ?? null,
                                      selected:
                                          option.value === control.value
                                              ? ''
                                              : null,
                                  },
                                  children: [option.content ?? ''],
                              })
                          )
                        : null;

                return {
                    tagName: 'select',
                    attributes: control.attributes,
                    classList: control.classList || {},
                    ...(children ? { children } : {}),
                };
            }
            case 'radio': {
                const children =
                    'options' in control && control.options
                        ? control.options.map((option): ElementConfig => {
                              const optionAttributes =
                                  (option.attributes as Record<
                                      string,
                                      unknown
                                  >) ?? {};
                              const optionId =
                                  ('id' in option && option.id) ||
                                  optionAttributes.id ||
                                  option.value ||
                                  null;

                              return {
                                  tagName: 'li',
                                  children: [
                                      {
                                          tagName: 'input',
                                          classList: option.classList,
                                          attributes: mergeAttributes(
                                              {
                                                  type: 'radio',
                                                  name:
                                                      option.name ??
                                                      optionAttributes.name ??
                                                      null,
                                                  value: option.value ?? null,
                                                  id: optionId,
                                                  checked:
                                                      option.value ===
                                                      control.value
                                                          ? ''
                                                          : null,
                                              },
                                              optionAttributes
                                          ),
                                      },
                                      {
                                          tagName: 'label',
                                          attributes: {
                                              for: optionId,
                                          },
                                          children: [option.content ?? ''],
                                      },
                                  ],
                              };
                          })
                        : null;

                return {
                    tagName: 'ul',
                    classList: {
                        'input-list': true,
                    },
                    ...(children ? { children } : {}),
                };
            }
            case 'radio-horizontal':
                return 'options' in control && !!control.options
                    ? control.options.reduce<Array<ElementConfig>>(
                          (elements, option) => {
                              const optionAttributes =
                                  (option.attributes as Record<
                                      string,
                                      unknown
                                  >) ?? {};
                              const optionId =
                                  option.id ??
                                  (optionAttributes.id as string | undefined) ??
                                  option.value ??
                                  null;
                              const inputAttributes = {
                                  type: 'radio',
                                  name:
                                      option.name ??
                                      (optionAttributes.name as
                                          | string
                                          | undefined) ??
                                      null,
                                  value: option.value ?? null,
                                  id: optionId,
                                  checked:
                                      option.value === control.value
                                          ? ''
                                          : null,
                              } as AttributesConfig;

                              return elements.concat([
                                  {
                                      tagName: 'input',
                                      classList: option.classList,
                                      attributes: mergeAttributes(
                                          inputAttributes,
                                          optionAttributes
                                      ),
                                  },
                                  {
                                      tagName: 'label',
                                      attributes: {
                                          for: optionId,
                                      },
                                      children: [option.content ?? ''],
                                  },
                              ]);
                          },
                          []
                      )
                    : null;
            case 'checkbox-horizontal':
                return 'options' in control && !!control.options
                    ? control.options.reduce<Array<ElementConfig>>(
                          (elements, option) => {
                              const optionAttributes =
                                  (option.attributes as Record<
                                      string,
                                      unknown
                                  >) ?? {};
                              const optionId =
                                  option.id ??
                                  (optionAttributes.id as string | undefined) ??
                                  option.value ??
                                  null;
                              const inputAttributes = {
                                  type: 'checkbox',
                                  name:
                                      option.name ??
                                      (optionAttributes.name as
                                          | string
                                          | undefined) ??
                                      undefined,
                                  value: option.value,
                                  id: optionId,
                                  checked:
                                      option.checked ||
                                      option.value === control.value
                                          ? ''
                                          : null,
                              } as AttributesConfig;

                              return elements.concat([
                                  {
                                      tagName: 'input',
                                      classList: option.classList,
                                      attributes: mergeAttributes(
                                          inputAttributes,
                                          optionAttributes
                                      ),
                                  },
                                  {
                                      tagName: 'label',
                                      attributes: {
                                          for: optionId,
                                      },
                                      children: [option.content ?? ''],
                                  },
                              ]);
                          },
                          []
                      )
                    : null;
            case 'inputgroup':
                if (control.control) {
                    inputControls.push(control.control);
                }
                if (control.controls) {
                    Object.keys(control.controls).forEach((key) => {
                        inputControls.push(control.controls![key]);
                    });
                }
                return {
                    tagName: 'ui-inputgroup',
                    attributes: control.attributes,
                    children: inputControls.map((inputControl) =>
                        UIField.buildControlFromConfig(inputControl)
                    ) as ElementChildren,
                };
            case 'autocomplete':
                return UIAutocompleteHelpers.fromControlConfig(control);
            case 'dropdown':
                return UIDropdownHelpers.fromControlConfig(control);
            case 'slider':
                return UISliderHelpers.fromControlConfig(control);
            case 'date':
                return UIDatePickerHelpers.fromControlConfig(control);

            default:
                return null;
        }
    }

    /**
     * Set object data to nested controls, where the name attribute is a key property.
     * @param {ElementControlsData} data Data to be applied to the node.
     * @returns {UIField}
     */
    setData(data: ElementControlsData): this {
        setData(this, data);
        return this;
    }

    /**
     * Get object data from nested controls, where the name attribute is a key property.
     * @returns {ElementControlsData}
     */
    getData(): ElementControlsData {
        return getData(this);
    }

    /**
     * Adds validation error under the field.
     * @param {string} message
     * @param {string} errorType
     * @param {boolean} [announce]
     * @returns {UIField}
     */
    addError(message: string, errorType: string, announce = true): this {
        this.addHint(
            {
                type: UIHint.types.ERROR,
                content: message,
                errorType: errorType,
            },
            announce
        );
        this.updateAriaDescribedBy();
        return this;
    }

    /**
     * Adds validation error under the field.
     * @param {string} message
     * @param {string} errorType
     * @returns {UIField}
     */
    setError(message: string, errorType: string): this {
        const prevError = this.querySelector(
            'ui-hint[error-type=' + errorType + ']'
        );
        if (prevError instanceof UIHint) {
            prevError.setMessage?.(message);
        } else {
            this.addError(message, errorType);
        }
        return this;
    }

    /**
     * Remove all direct children hints from the field.
     * If param type is provided, only hints of that type will be removed.
     * @param {string | null} [type] Type of the ui-hint.
     * @returns {UIField}
     */
    removeHints(type: string | null = null): this {
        let hintsSelector = [
            ':scope > ui-hint',
            ':scope .ui-field__control > ui-hint',
        ];
        if (type) {
            hintsSelector = hintsSelector.map(
                (selector) => `${selector}[type="${type}"]`
            );
        }
        const hints = this.querySelectorAll(hintsSelector.join(','));
        hints.forEach((hint) => hint.remove());
        this.checkErrors();
        return this;
    }

    /**
     * Adds hint under the field.
     * @param {FieldHint} hint
     * @param {boolean} [announce] If true, screen readers will announce error hints immediately.
     * @returns {UIField}
     */
    addHint(hint: FieldHint, announce = true): this {
        const children: Array<ElementConfig> = [
            {
                tagName: 'span',
                children: [hint.content ?? ''],
            },
        ];

        const isErrorAnnouncement =
            hint.type === UIHint.types.ERROR && announce;
        const hintElement = createElement<UIHint>({
            tagName: 'ui-hint',
            attributes: {
                'type': hint.type,
                'error-type': hint.errorType || null,
                'role': isErrorAnnouncement ? 'alert' : null,
                'aria-live': isErrorAnnouncement ? null : 'polite',
                'aria-atomic': isErrorAnnouncement ? null : 'true',
            },
            children,
        })!;

        if (hint.type === UIHint.types.ERROR) {
            const textCounter = this.control?.querySelector(
                'ui-textcounter'
            ) as (HTMLElement & { textarea?: HTMLElement }) | null;
            if (textCounter) {
                textCounter.textarea?.insertAdjacentElement(
                    'afterend',
                    hintElement
                );
                return this;
            }
            const firstNonErrorHint = this.control?.querySelector(
                `:scope > ui-hint:not([type="${UIHint.types.ERROR}"])`
            );
            if (firstNonErrorHint) {
                this.control!.insertBefore(hintElement, firstNonErrorHint);
            } else {
                this.control?.appendChild(hintElement);
            }
        } else {
            this.control?.appendChild(hintElement);
        }
        this.checkErrors();
        return this;
    }

    /**
     * Creates an add button trailing element
     * @fires event:ui-field-add
     * @private
     * @returns {HTMLElement}
     */
    createAddButton(): HTMLElement {
        return element`
            <ui-button
                class="ui-field__trailing-action -add"
                aria-label="${UIField.labels.add}"
                glyph="redesign-circle-plus"
                data-event="ui-field-add"
                layout="tertiary"
            ></ui-button>
        `;
    }

    /**
     * Creates a remove button trailing element
     * @fires event:ui-field-remove
     * @private
     * @returns {HTMLElement}
     */
    createRemoveButton(): HTMLElement {
        return element`
            <ui-button
                class="ui-field__trailing-action -remove"
                aria-label="${UIField.labels.remove}"
                glyph="redesign-circle-x-mark"
                data-event="ui-field-remove"
                layout="tertiary"
            ></ui-button>
        `;
    }

    /**
     * Check if a control element is visible (ignores class=-hidden, type, and hidden attribute).
     * @param {HTMLElement} el The element to test
     * @returns {boolean} True if element is not hidden by class, type, or attribute
     */
    isVisibleControl(el: HTMLElement): boolean {
        return (
            !el.classList.contains('-hidden') &&
            el.getAttribute('type') !== 'hidden' &&
            !el.hasAttribute('hidden')
        );
    }

    /**
     * Updates label content. Also
     * adds / removes an asterisk to indicate if field is required.
     * @returns {UIField}
     */
    updateLabel(): this {
        const hasLabelText = !!(this.label && String(this.label).trim());
        const requiredMarker =
            this.required && hasLabelText
                ? this.renderRequiredMarker(!this.isDropdown())
                : null;

        if (!hasLabelText && !requiredMarker) {
            this.updateAriaLabels();
            return this;
        }

        this.setLabelContent(
            [this.label, requiredMarker].filter(Boolean) as Array<ElementChild>
        );
        this.updateAriaLabels();
        return this;
    }

    /**
     * Ensures the trailing action is placed inside <ui-inputgroup>.
     * Moves non-hint children into the input group and appends the trailing action.
     * @param {HTMLElement | null} trailingAction
     */
    applyTrailingActionToControl(trailingAction: HTMLElement | null): void {
        if (this.trailingAction) {
            this.trailingAction.remove();
        }

        if (!trailingAction) {
            return;
        }

        let inputGroup: HTMLElement | null = this.inputGroup ?? null;
        if (!inputGroup) {
            inputGroup = createElement<HTMLElement>({
                tagName: 'ui-inputgroup',
                classList: {
                    'ui-field__inputgroup': true,
                },
            })!;

            const children = Array.from(this.control!.childNodes);
            children.forEach((node) => {
                if (
                    !(node instanceof Element) ||
                    node.localName !== 'ui-hint'
                ) {
                    inputGroup!.appendChild(node);
                }
            });

            const firstHint = this.control!.querySelector(
                ':scope > ui-hint:first-of-type'
            );
            if (firstHint) {
                this.control!.insertBefore(inputGroup, firstHint);
            } else {
                this.control!.appendChild(inputGroup);
            }
        }
        if (!this.inputGroup?.contains(trailingAction)) {
            this.inputGroup?.appendChild(trailingAction);
        }
    }

    /**
     * Updates the trailing action button in the control wrapper.
     */
    updateTrailingAction(): void {
        const trailingAction = this.getTrailingAction();
        this.applyTrailingActionToControl(trailingAction);
    }

    /**
     * Updates aria-labels for date/time components to include the field label context
     * @private
     */
    updateAriaLabels(): void {
        const control = this.getControl();
        if (!control && !this.label) {
            return;
        }

        if (this.shouldPlaceRequiredMarkerAtCheckboxLabel()) {
            (control as HTMLElement)?.nextElementSibling?.appendChild(
                this.createElement(this.renderRequiredMarker(true))
            );
        }

        updateAriaLabels(control as HTMLElement, this.label);
    }

    /**
     * Check if required marker should be rendered at checkbox label (instead of host).
     * @returns {boolean}
     */
    shouldPlaceRequiredMarkerAtCheckboxLabel(): boolean {
        return (
            this.hasSingleCheckboxControl() &&
            !this.label &&
            !!this.required &&
            !this.querySelector('.ui-field__required-marker')
        );
    }

    /**
     * Check if field has error hints inside.
     * @returns {UIField}
     */
    checkErrors(): this {
        const hasError =
            this.querySelectorAll(
                `ui-hint[type=${UIHint.types.ERROR}]:not(.-hidden)`
            ).length > 0;
        this.updateClassList({ '-error': hasError });
        const control = this.querySelector('input,textarea,select');
        if (!control) {
            return this;
        }
        if (hasError) {
            control.setAttribute('aria-invalid', 'true');
        } else {
            control.removeAttribute('aria-invalid');
        }

        const dropdownControl = this.querySelector('.ui-dropdown__control');
        if (dropdownControl) {
            if (hasError) {
                dropdownControl.setAttribute('aria-invalid', 'true');
            } else {
                dropdownControl.removeAttribute('aria-invalid');
            }
        }

        return this;
    }

    /**
     * Gets first validation error for field.
     * @returns {Element | null}
     */
    getError(): Element | null {
        return (
            this.querySelector(`ui-hint[type=${UIHint.types.ERROR}]`) || null
        );
    }

    /**
     * Gets all validation errors for field.
     * @returns {NodeList}
     */
    getAllErrors(): NodeListOf<Element> {
        return this.querySelectorAll(`ui-hint[type=${UIHint.types.ERROR}]`);
    }

    /**
     * Remove all validation errors. Errors are hints and only hints with type="errors"
     * will be removed.
     * @param {boolean} [force] Remove errors with force and ignores all other conditions.
     * @returns {UIField}
     */
    removeErrors(force = false): this {
        this.querySelectorAll(
            `.ui-field__control ui-hint[type=${UIHint.types.ERROR}]`
        ).forEach((node) => {
            const hintNode = node as Element & {
                errorType?: string;
                parentElement: HTMLElement | null;
            };
            const elem =
                hintNode.parentElement?.querySelector<HTMLInputElement>(
                    'input,textarea,select'
                );

            if (!elem) {
                return;
            }

            if (
                force ||
                (elem.validity?.valid && hintNode.errorType) ||
                (elem.dataset.errorType &&
                    elem.dataset.errorType === hintNode.errorType)
            ) {
                this.updateClassList({ '-error': false });
                elem.removeAttribute('aria-invalid');
                elem.removeAttribute('aria-describedby');
                hintNode.parentElement?.removeChild(node);
            }
        });
        return this;
    }

    /**
     * Reads assistive error for a11y.
     */
    announceError(): void {
        const err = this.getError();
        if (err) {
            err.setAttribute('aria-live', 'polite');
        }
    }

    /**
     * Get id of the field control/input. Used to set label[for] so the label is associated with the control.
     *
     * Who assigns the id:
     * - Native control (input/select/textarea directly in ui-field): ui-field owns the id. If missing, ui-field
     *   generates one and sets it on the element. ui-field then sets label[for] to that id.
     * - Custom control (ui-datepicker, ui-slider, ui-daterange, etc.): The component owns the id. The component
     *   sets id on its internal element(s) (user id > control-id > generated) and exposes it via
     *   getControlIdForField(). ui-field only reads that id and sets label[for]. ui-field does not set id on
     *   elements inside a custom component (that would create timing/ownership confusion).
     *
     * Resolution order:
     * 1. If control is custom and implements getControlIdForField(), use that (component owns id).
     * 2. If control is native (direct child), use/generate id and set on element (ui-field owns id).
     * 3. Legacy fallback: slotted native inside custom, or control-id, or generated (see implementation).
     * @returns {string | null}
     */
    getControlId(): NullOr<string> {
        let control = this.getControl();
        if (!control) {
            return null;
        }

        control = this.resolveControlForId(control);

        // 1) Custom component owns id (getControlIdForField)
        const componentId =
            typeof (control as Partial<ControlWithFieldId>)
                .getControlIdForField === 'function'
                ? (control as ControlWithFieldId).getControlIdForField()
                : null;
        if (componentId) {
            return componentId;
        }

        // 2) Native form element directly in ui-field – field owns id
        if (BaseNativeFormElements.includes(control.localName)) {
            const id = control.getAttribute('id') || Digest.randomId();
            if (!control.getAttribute('id')) {
                control.setAttribute('id', id);
            }
            return id;
        }

        // 3) Slotted native element (input/select/textarea) inside the control. If the control is one of our custom form form components (ui-slider, ui-rating, etc.): only read an existing id; we never set id
        //    components (ui-slider, ui-rating, etc.): only read an existing id; we never set id
        //    on their children. If the control is something else (e.g. a div or unknown custom
        //    element wrapping an input): set id on the inner element from control-id or generate.
        const fromSlotted = this.getControlIdFromSlotted(control);
        if (fromSlotted !== undefined) {
            return fromSlotted;
        }

        // 4) control-id attribute on the element
        const controlIdAttr = control.getAttribute('control-id');
        if (controlIdAttr) {
            return controlIdAttr;
        }

        // 5) Fallback: generate id and optionally apply
        const newId = Digest.randomId();
        const isCustom = CustomFormElements.includes(control.localName);

        if (!isCustom) {
            const fallbackNative = control.querySelector(
                BaseNativeFormElements.map(
                    (sel) => `${sel}:not([type="hidden"])`
                ).join(',')
            );
            if (fallbackNative) {
                fallbackNative.setAttribute('id', newId);
                return newId;
            }
        }

        if (isCustom) {
            // Not yet upgraded/rendered; linkLabels() will run again later
            return null;
        }

        control.setAttribute('control-id', newId);
        return newId;
    }

    /**
     * When getControl() returns an input/select/textarea that is inside a custom form component
     * (e.g. the range input inside ui-slider), return that custom component instead so it can
     * provide the id via getControlIdForField(). Otherwise return the same control.
     * @param {Element} control - The control returned by getControl().
     * @private
     */
    private resolveControlForId(control: Element): Element {
        if (!BaseNativeFormElements.includes(control.localName)) {
            return control;
        }
        let parent = control.parentElement;
        while (parent && parent !== this && parent !== document.body) {
            if (
                CustomFormElements.includes(parent.localName) &&
                typeof (parent as Partial<ControlWithFieldId>)
                    .getControlIdForField === 'function'
            ) {
                return parent;
            }
            parent = parent.parentElement;
        }
        return control;
    }

    /**
     * Finds an input/select/textarea (not hidden) inside the control and returns its id when usable.
     * If the control is a custom form component (e.g. ui-slider): only read an existing id from
     * the inner element when it is not a generated id; do not set id here. Return undefined when there
     * is no usable id so getControlId() continues to the next step (e.g. control-id attribute).
     * If the control is a plain wrapper: ensure the inner element has an id (from control-id or
     * generated) and return it.
     * @param {Element} control - The wrapper element that may contain an input/select/textarea.
     * @private
     */
    private getControlIdFromSlotted(control: Element): string | undefined {
        const subControl = control.querySelector<HTMLElement>(
            NativeFormElements.map((sel) => `${sel}:not([type="hidden"])`).join(
                ','
            )
        );
        if (!subControl) {
            return undefined;
        }

        const isGeneratedId = (id: string): boolean =>
            !id || /^uid-[0-9a-f]{8}$/i.test(id);
        let subControlId = subControl.getAttribute('id') || subControl.id || '';

        if (CustomFormElements.includes(control.localName)) {
            if (subControlId && !isGeneratedId(subControlId)) {
                return subControlId;
            }
            return undefined;
        }

        const controlIdAttr = control.getAttribute('control-id');
        if (!subControlId || (controlIdAttr && isGeneratedId(subControlId))) {
            subControlId = controlIdAttr || subControlId || Digest.randomId();
            subControl.id = subControlId;
            subControl.setAttribute('id', subControlId);
        }
        return subControlId;
    }

    /**
     * Renders required marker HTML.
     * @param {boolean} [ariaHidden] When true, excludes the marker from screen reader traversal.
     * @returns {ElementConfig}
     */
    renderRequiredMarker(ariaHidden = false): ElementConfig {
        const requiredLabel =
            typeof UIField.labels.required === 'string'
                ? UIField.labels.required
                : '';
        return {
            tagName: 'span',
            classList: {
                'ui-field__required-marker': true,
            },
            attributes: {
                'aria-hidden': ariaHidden,
            },
            children: [` (${requiredLabel})`],
        };
    }

    /**
     * Gets the control. This will be either a native form element inside of the component host or a slotted custom element.
     * @param {boolean} [includeFieldsetFriendly] Include fieldset-friendly HTML elements (radios or checkboxes)
     * @returns {Element | null}
     */
    getControl(includeFieldsetFriendly = false): Element | null {
        const baseNative = includeFieldsetFriendly
            ? BaseNativeFormElements
            : NativeFormElements;
        const selectors = CustomFormElements.concat(baseNative).join(',');
        return this.querySelector(selectors);
    }

    /**
     * Get trailing action.
     * @returns {HTMLElement | null}
     */
    getTrailingAction(): HTMLElement | null {
        let trailingAction: HTMLElement | null = null;
        if (this.actionAdd) {
            trailingAction = this.createAddButton();
        } else if (this.actionRemove) {
            trailingAction = this.createRemoveButton();
        } else {
            trailingAction =
                keepAllowedElements(
                    this.getChildrenForSlot('trailing'),
                    UIField.allowedTrailingElementTags
                )[0] ?? null;
            if (trailingAction) {
                trailingAction.classList.add('ui-field__trailing-action');
            }
        }
        return trailingAction;
    }

    getControlElements(): Array<ElementChild> {
        const children = Array.from(this.childNodes).filter(
            (node) =>
                node.nodeType !== Node.ELEMENT_NODE ||
                !(node as Element).hasAttribute('slot')
        );

        const isTextOnlyField = this.classList.contains('-text');
        const isTextOnlyContent = children.every(
            (node) => node.nodeType === Node.TEXT_NODE
        );

        if (!isTextOnlyField || !isTextOnlyContent) {
            return children;
        }

        const textOnlyChildren = children.filter(
            (node) => node.nodeType === Node.TEXT_NODE
        );
        const textContent = textOnlyChildren
            .map((node) => node.textContent)
            .join('')
            .trim();

        return [
            {
                tagName: 'input',
                classList: {
                    '-visually-hidden': true,
                },
                attributes: {
                    type: 'text',
                    value: textContent,
                    readonly: true,
                    tabIndex: '-1',
                },
            },
            {
                tagName: 'span',
                attributes: {
                    'aria-hidden': 'true',
                },
                children: textOnlyChildren,
            },
            ...children.filter((node) => node.nodeType === Node.ELEMENT_NODE),
        ];
    }

    /**
     * Gets the number of controls in the field.
     * @param {boolean} [includeFieldsetFriendly] Include fieldset-friendly HTML elements (radios or checkboxes)
     * @returns {number}
     */
    getControlsLength(includeFieldsetFriendly = false): number {
        const selectors = (
            includeFieldsetFriendly
                ? BaseNativeFormElements
                : NativeFormElements
        )
            .concat(CustomFormElements)
            .join(',');
        return this.querySelectorAll(selectors).length;
    }

    /**
     * Sets content to label element inside ui-field.
     * @param {ElementChildren | null} children Content to be set inside label.
     * @returns {UIField}
     */
    setLabelContent(children: ElementChildren | null): this {
        const control = this.getControl(true);
        const isFieldsetFriendly = this.isFieldsetFriendlyControl();

        let labelEl = this.querySelector<HTMLElement>('.ui-field__label-inner');
        if (!labelEl) {
            if (!children) {
                return this;
            }
            const controlId = this.getControlId();
            const labelAttributes: Record<string, string | null> = {};
            if (controlId && this.isDropdown(control)) {
                labelAttributes.id = `${controlId}-label`;
            }
            labelEl = this.createElement({
                tagName: isFieldsetFriendly ? 'span' : 'label',
                attributes: labelAttributes,
                classList: { 'ui-field__label-inner': true },
            });
            if (controlId) {
                labelEl.setAttribute('for', controlId);
            }
            const container = this.labelContainer;
            if (container) {
                if (container.childNodes.length > 0) {
                    container.insertBefore(labelEl, container.firstChild);
                } else {
                    container.appendChild(labelEl);
                }
            }
        }

        labelEl.innerHTML = '';
        updateElement(
            labelEl,
            {
                children: children ?? [],
            },
            true
        );

        const labelUnsafeTooltip = [...labelEl.children].find((child) =>
            child.matches('ui-tooltip')
        );
        if (getDebugMode() && labelUnsafeTooltip) {
            console.warn(
                'ui-tooltip was passed through label as unsafe html. This functionality will be deprecated starting April 2027. Use slots for ui-tooltip instead. For example',
                '<ui-tooltip slot="label">I am a label</ui-tooltip>',
                'Related task: https://jira.swedbank.net/browse/GUI-4282',
                'Violating element:',
                labelUnsafeTooltip
            );
        }

        const needsProgrammaticHandler =
            control && CustomFormElements.includes(control.localName);

        if (needsProgrammaticHandler) {
            if (labelEl?.parentNode) {
                if (this._labelClickListener) {
                    this._labelClickListener.label.removeEventListener(
                        'click',
                        this._labelClickListener.handler
                    );
                    this._labelClickListener = null;
                }
            }

            const clickHandler = (e: Event): void => {
                void handleLabelClick(
                    e as PointerEvent,
                    control as HTMLElement
                );
            };
            labelEl.addEventListener('click', clickHandler);
            this._labelClickListener = {
                label: labelEl,
                handler: clickHandler,
            };
        }

        return this;
    }

    /**
     * Checks if the given control is a ui-dropdown element.
     * @param {HTMLElement} [control] - The control element to check. If not provided, uses getControl().
     * @returns {boolean} True if the control is a ui-dropdown, false otherwise.
     */
    isDropdown(control?: HTMLElement | Element | null): boolean {
        const controlElement = control ?? this.getControl();
        return !!(controlElement?.localName === 'ui-dropdown');
    }

    /**
     * Links labels to controls.
     * @returns {UIField}
     */
    linkLabels(): this {
        const label = this.querySelector<HTMLElement>('.ui-field__label-inner');
        if (!label) {
            return this;
        }
        const controlId = this.getControlId();
        if (controlId) {
            if (this.isDropdown()) {
                label.setAttribute('id', `${controlId}-label`);
            }
            label.setAttribute('for', controlId);
            const currencyLabel =
                this.querySelector<HTMLElement>('label.units');
            if (currencyLabel) {
                currencyLabel.setAttribute('for', controlId);
            }
        }
        this.updateAriaLabels();
        this.querySelectorAll<UIDropdown>('ui-dropdown').forEach((dropdown) =>
            dropdown.syncFieldAriaReferences?.()
        );
        return this;
    }

    /**
     * Updates the aria-describedby attribute for the field based on a prop value or, if missing, a control ID.
     * @private
     * @returns {void}
     */
    updateAriaDescribedBy(): void {
        const propValue = this.getAttribute('described-by-id');

        if (propValue) {
            this.setAriaDescribedByOnInput(propValue.split(' '));
        } else {
            const ids: Array<string> = [];

            const firstRadioOrCheckbox =
                this.control?.querySelector<HTMLInputElement>(
                    'input[type="radio"], input[type="checkbox"]'
                );
            const controlId =
                this.getControlId() ||
                (firstRadioOrCheckbox?.id ? firstRadioOrCheckbox.id : null);

            if (controlId) {
                const describedByIdBase = `${controlId}-hint`;
                const hints = Array.from(
                    this.control?.querySelectorAll('ui-hint') ?? []
                );
                const existingIds = new Set(
                    hints.map((h) => h.id).filter(Boolean)
                );
                let nextIndex = 0;
                hints.forEach((hintEl) => {
                    if (!hintEl.id) {
                        while (
                            existingIds.has(`${describedByIdBase}-${nextIndex}`)
                        ) {
                            nextIndex++;
                        }
                        const newId = `${describedByIdBase}-${nextIndex}`;
                        hintEl.setAttribute('id', newId);
                        existingIds.add(newId);
                        nextIndex++;
                    }
                    ids.push(hintEl.id);
                });
            }

            this.setAriaDescribedByOnInput(ids);
        }
    }

    /**
     * Sets the aria-describedby attribute on the control element.
     * @param {string[]} ids - The ID or IDs to set as the aria-describedby attribute.
     * Must correspond to a ui-hint, ui-tooltip, or other describing element's unique ID.
     * @private
     * @returns {void}
     */
    setAriaDescribedByOnInput(ids: Array<string>): void {
        const inputEls = [
            'input:not([type=radio]):not([type=checkbox]):not(.ui-stepper__control):not(.ui-slider__control):not(.ui-monthpicker__input):not(.ui-rating__control)',
            'input[type="checkbox"]:only-of-type',
            ':scope > fieldset',
            'ui-rating fieldset',
            'select:not(.ui-dropdown__select)',
            'textarea',
            '.ui-dropdown__control',
            '.ui-slider__handle',
        ];
        const input = this.querySelector<HTMLElement>(inputEls.join(','));
        if (input) {
            const currentDescribedBy =
                input.getAttribute('aria-describedby') || '';
            const newDescribedBy = [
                ...new Set([...currentDescribedBy.split(' '), ...ids]),
            ]
                .join(' ')
                .trim();
            if (newDescribedBy) {
                input.setAttribute('aria-describedby', newDescribedBy);
            }
        }
    }

    /**
     * Moves the trailing action to the appropriate place based on the screen size.
     */
    moveTrailingAction(): void {
        if (!this.trailingAction || this.actionAdd || this.actionRemove) {
            return;
        }
        const isMobile = browser.mobileMediaMatches();
        const isFluid = this.layout === UIField.Layout.Fluid;
        if (!isFluid && !isMobile) {
            this.inputGroup?.appendChild(this.trailingAction);
        } else {
            this.control?.appendChild(this.trailingAction);
        }
    }

    /**
     * Checks if the HTML control passed through the default slot is a radio or checkbox that needs a fieldset wrapper.
     * @returns {boolean}
     */
    isFieldsetFriendlyControl(): boolean {
        const control = this.getControl(true);
        const controlsLength = this.getControlsLength(true);
        return (
            !!control &&
            ['radio', 'checkbox'].includes(
                (control as HTMLInputElement).type ?? ''
            ) &&
            controlsLength > 1
        );
    }

    /**
     * Checks if the HTML control passed through the default slot is a single checkbox.
     * @returns {boolean}
     */
    hasSingleCheckboxControl(): boolean {
        const control = this.getControl(true);
        const controlsLength = this.getControlsLength(true);
        return (
            !!control &&
            (control as HTMLInputElement).type === 'checkbox' &&
            controlsLength === 1
        );
    }

    /**
     * @inheritDoc
     */
    observeAttributes(
        name: AttributeValue,
        oldValue: AttributeValue,
        newValue: NullOr<string>
    ): void {
        /* istanbul ignore if */
        if (!this.hydrated) {
            return;
        }

        switch (name) {
            case 'required':
                if (oldValue !== null) {
                    this.removeErrors(true);
                }
            // fallthrough
            case 'label':
                this.updateLabel();
                break;
            case 'action-add':
            case 'action-remove':
                this.updateTrailingAction();
                break;
            case 'units':
                this.updateUnits();
                break;
        }
    }

    /**
     * Extracts units from the markup if present (legacy approach).
     */
    getUnitsFromMarkup(): void {
        const unitsElement = this.querySelector<HTMLElement>('.units');
        if (unitsElement && !this.classList.contains('-text')) {
            const nativeControlsSelector =
                'input:not([type="checkbox"]):not([type="radio"]), select';
            let prev =
                unitsElement.previousElementSibling as HTMLElement | null;
            while (prev && !this.isVisibleControl(prev)) {
                prev = prev.previousElementSibling as HTMLElement | null;
            }
            if (prev?.matches(nativeControlsSelector)) {
                this.units = unitsElement.textContent ?? undefined;
                unitsElement.remove();
            }
        }
    }

    /**
     * Update or build the units label inside the field if only a native input or select is present.
     */
    updateUnits(): void {
        const unitsAriaDescribedByPrefix = 'ui-field__units-';
        const nativeControlsSelector =
            ':scope > input:not([type="checkbox"]):not([type="radio"]), :scope > select';

        if (!this.units) {
            const control =
                this._unitsWrapper?.querySelector<HTMLElement>(
                    nativeControlsSelector
                ) ?? null;
            if (control?.hasAttribute('aria-describedby')) {
                const unitsId = control
                    .getAttribute('aria-describedby')!
                    .split(' ')
                    .find((id) => id.startsWith(unitsAriaDescribedByPrefix));
                if (unitsId) {
                    const describedBy =
                        control.getAttribute('aria-describedby') || '';
                    const ids = describedBy
                        .split(' ')
                        .filter((id) => id && id !== unitsId)
                        .join(' ');
                    if (ids) {
                        control.setAttribute('aria-describedby', ids);
                    } else {
                        control.removeAttribute('aria-describedby');
                    }
                }
            }
            if (this._unitsWrapper?.parentNode) {
                this._unitsWrapper.parentNode.insertBefore(
                    control!,
                    this._unitsWrapper
                );
                this._unitsWrapper.parentNode.removeChild(this._unitsWrapper);
                this._unitsWrapper = null;
            }
            return;
        }

        if (this._unitsWrapper && this.unitsElement) {
            this.unitsElement.textContent = this.units;
            return;
        }

        const controls = this.inputGroup
            ? this.inputGroup.querySelectorAll<HTMLElement>(
                  nativeControlsSelector
              )
            : this.control?.querySelectorAll<HTMLElement>(
                  nativeControlsSelector
              );

        if (!controls) {
            return;
        }

        let lastControl: HTMLElement | null = null;
        for (const ctrl of Array.from(controls).reverse()) {
            if (this.isVisibleControl(ctrl)) {
                lastControl = ctrl;
                break;
            }
        }

        if (!lastControl) {
            return;
        }

        const wrapper = document.createElement('span');
        wrapper.className = 'ui-field__units-wrapper';
        lastControl.parentNode!.insertBefore(wrapper, lastControl);
        wrapper.appendChild(lastControl);

        const unitsEl = document.createElement('span');
        unitsEl.className = 'ui-field__units';
        unitsEl.textContent = this.units;
        unitsEl.id = unitsAriaDescribedByPrefix + Digest.randomId();
        wrapper.appendChild(unitsEl);
        lastControl.setAttribute('aria-describedby', unitsEl.id);
        this._unitsWrapper = wrapper;
    }

    /**
     * Forces custom dropdown validation if the field is required.
     * @private
     */
    private forceCustomDropdownValidationIfRequired(): void {
        const customDropDown = this.getControl();
        if (
            customDropDown?.localName === 'ui-dropdown' &&
            'nativeSelect' in customDropDown
        ) {
            const nativeSelect = customDropDown.nativeSelect;
            if (
                nativeSelect instanceof HTMLSelectElement &&
                nativeSelect.required
            ) {
                nativeSelect.dataset.forceValidation = '1';
            }
        }
    }

    /**
     * @inheritDoc
     */
    override render(): void {
        const inputGroupElement =
            this.querySelector<HTMLElement>('ui-inputgroup');
        if (inputGroupElement) {
            inputGroupElement.classList.add('ui-field__inputgroup');
        }

        const isFieldsetFriendly = this.isFieldsetFriendlyControl();
        const controlId = this.getControlId();

        const legendElementConfig: ElementConfig = {
            tagName: 'legend',
            classList: {
                '-visually-hidden': true,
            },
            children: [this.label ?? ''],
        };

        const labelElementConfig: ElementConfig = {
            tagName: 'div',
            classList: {
                'ui-field__label': true,
            },
            attributes: {
                'aria-hidden': isFieldsetFriendly && this.label ? 'true' : null,
            },
            children: (
                [
                    !!this.label && {
                        tagName: isFieldsetFriendly ? 'span' : 'label',
                        classList: {
                            'ui-field__label-inner': true,
                        },
                        attributes: {
                            id:
                                controlId && this.isDropdown(this.getControl())
                                    ? `${controlId}-label`
                                    : null,
                            for: controlId || null,
                        },
                        children: [
                            this.label,
                            this.required &&
                                this.renderRequiredMarker(!this.isDropdown()),
                        ],
                    },
                ] as Array<ElementChild>
            ).concat(this.getChildrenForSlot('label')),
        };

        const trailingAction = this.getTrailingAction();

        const controlElementConfig: ElementConfig = {
            tagName: 'div',
            classList: {
                'ui-field__control': true,
            },
            children: this.getControlElements(),
        };

        const wrapperElementConfig: ElementConfig = {
            tagName: isFieldsetFriendly && this.label ? 'fieldset' : 'div',
            classList: {
                'ui-field__wrapper': true,
            },
            children: [
                isFieldsetFriendly && this.label && legendElementConfig,
                labelElementConfig,
                controlElementConfig,
            ] as Array<ElementChild>,
        };

        this.updateElement({
            children: [wrapperElementConfig],
        });

        this.applyTrailingActionToControl(trailingAction);
        this.getUnitsFromMarkup();

        if (this.classList.contains('-text')) {
            this.linkLabels();
        }
        this.updateUnits();
        this.updateAriaDescribedBy();
        this.forceCustomDropdownValidationIfRequired();
    }

    /**
     * @inheritDoc
     */
    override hydrate(): void {
        this.checkErrors();
        this.updateLabel();
        this.linkLabels();
        this.updateAriaLabels();
        this.resizeHandler = debounce(this.moveTrailingAction.bind(this), 200);
        window.addEventListener('resize', this.resizeHandler);
        this.moveTrailingAction();
    }

    /**
     * @inheritDoc
     */
    override disconnect(): void {
        if (this.resizeHandler) {
            window.removeEventListener('resize', this.resizeHandler);
        }
        if (this._labelClickListener) {
            this._labelClickListener.label.removeEventListener(
                'click',
                this._labelClickListener.handler
            );
            this._labelClickListener = null;
        }
    }
}
